import { GridColDef, GridRowId } from '@mui/x-data-grid';
import { useEffect, useMemo, useState } from 'react';
import DataTable from '../../../../components/table/datatable';
import { UseDisclosureInterface } from '../../../../hooks/useDisclosure';
import { ModalWrapper } from '../../../../hooks/useModal';
import useToast from '../../../../hooks/useToast';
import {
  Autocomplete,
  Button,
  debounce,
  Stack,
  TextField,
} from '@mui/material';
import ControlledDatepicker from '@components/form/controlled/controlled-datepicker';
import moment, { Moment } from 'moment';
import useAppContext from '@hooks/useAppContext';
import { useAppSelector } from '@store/index';
import { useLazyGetAllShift } from '@api/pos/pos-work-period.api.rtk';
import {
  selectPOSPrinter,
  selectPOSPrinterMasterlist,
  selectPOSstationID,
} from '@modules/point-of-sale/slice/pos-selector';
import ControlledAutocomplete from '@components/form/controlled/controlled-autocomplete';
import { Print } from '@mui/icons-material';
import { PrinterDetails } from '@modules/point-of-sale/interface/bo-interface/printers.interface';
import { useLazyGetXReadingQuery } from '@api/pos/pos-bo-reports.api.rtk';

const columns: GridColDef[] = [
  {
    field: 'salesDate',
    headerName: 'Sales Date',
    minWidth: 150,
    flex: 1,
  },
  {
    field: 'stationName',
    headerName: 'Shift Caption',
    minWidth: 200,
    flex: 1,
  },
  {
    field: 'statedBy',
    headerName: 'Shift Operator',
    minWidth: 250,
    flex: 1,
  },
  {
    field: 'workPeriod',
    headerName: 'Period',
    minWidth: 200,
    flex: 1,
  },
];

const POSShiftReportModal = ({
  modalDisclosure,
}: {
  modalDisclosure: UseDisclosureInterface;
}) => {
  const [selectedDate, setSelectedDate] = useState<Moment | null>(null);
  const [cashier, setCashier] = useState<string>('');
  const [selectedRows, setSelectedRows] = useState<GridRowId[]>([]);
  const stationID = useAppSelector(selectPOSstationID);
  const printerID = useAppSelector(selectPOSPrinter);

  // Priting
  const [printer, setPrinter] = useState<PrinterDetails | null>(null);
  const posPrinters = useAppSelector(selectPOSPrinterMasterlist);
  const [xReading, { isError: isXReadingError, error: xReadingError }] =
    useLazyGetXReadingQuery();

  const debouncedSetCashier = useMemo(
    () =>
      debounce((value: string) => {
        setCashier(value);
      }, 30),
    [],
  );

  const [
    fetchShift,
    { data: shiftData, isError, error, isLoading, isFetching },
  ] = useLazyGetAllShift();

  useEffect(() => {
    if (!stationID) return;

    fetchShift({
      stationID,
      salesDate:
        selectedDate === null
          ? 'All'
          : moment(selectedDate).format('YYYY-MM-DD'),
      cashier,
    });
  }, [stationID, selectedDate, cashier]);

  const shiftDataList =
    shiftData?.results.map((item) => ({
      ...item,
      salesDate: moment(item.salesDate).format('MMM DD, YYYY'),
    })) || [];
  const { toast } = useToast();
  const {
    openSecCodeDialog,
    //  startLoadingStatus,
    stopLoadingStatus,
  } = useAppContext();

  // ENABLE IF NEEDED - used to set the first value in the printerList
  // useEffect(() => {
  //   if (!posPrinters || posPrinters.length === 0) return;
  //   setPrinter(posPrinters[0]);
  // }, [posPrinters]);
  useEffect(() => {
    if (!isError) return;

    toast({
      message: error,
      severity: 'error',
    });
  }, [isError]);

  const handleRePrint = async () => {
    try {
      openSecCodeDialog(async (res) => {
        if (!res?.data.isValid) return;
        const shiftID = String(selectedRows[0]);
        // startLoadingStatus('Reprinting Shift Report');
        await xReading({
          shiftID,
          printerRefID: printer?.printerID || printerID || '',
        }).unwrap();

        if (isXReadingError) {
          toast({
            severity: 'error',
            message: xReadingError,
          });
          return;
        } else {
          toast({
            severity: 'success',
            message: 'X Reading Report reprinted successfully',
          });
        }
        stopLoadingStatus();
      }, 'access_pos_module');
    } catch (error) {
      toast({ message: error, severity: 'warning' });
    }
  };

  return (
    <ModalWrapper
      {...{
        modalDisclosure,
        disableOnClose: false,
        withHeader: {
          title: 'Shift Sales Reports - Reprint',
          learnMore: () => {},
          onClose: modalDisclosure.onClose,
        },
      }}
    >
      <Stack sx={{ height: '100%', width: '100%', overflow: 'hidden' }}>
        <Stack direction='row' gap={1} pt={1} width='100%'>
          <ControlledDatepicker
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            label='Filter by Date'
            isDataTable={false}
            sx={{
              width: '100%',
            }}
          />

          <TextField
            size='small'
            label='Filter by Cashier Name'
            fullWidth
            value={cashier}
            onChange={(e) => debouncedSetCashier(e.target.value)}
          />

          <Autocomplete
            options={['XR Summarized', 'XR Itemized', 'Shift Inventory']}
            size='small'
            renderInput={(params) => (
              <TextField {...params} label='Reprint What' />
            )}
            fullWidth
          />

          <ControlledAutocomplete
            options={posPrinters}
            loading={isLoading}
            getOptionLabel={(option) => `${option?.logicalDeviceName}`}
            selectedValue={printer}
            setSelectedValue={setPrinter}
            label='Reprint Where'
            size='small'
            sx={{
              minWidth: 200,
              '& .MuiInputBase-root': {
                border: '',
              },
              '& .MuiOutlinedInput-root': {
                '& > fieldset': {
                  borderColor: '',
                },
              },
            }}
            disableClearable
          />

          <Button
            variant='contained'
            sx={{ ml: 1, minWidth: 'max-content' }}
            onClick={handleRePrint}
            startIcon={<Print />}
            disabled={selectedRows.length === 0}
          >
            Reprint
          </Button>
        </Stack>
        <DataTable
          getRowId={(row) => row.shiftRefID}
          columns={columns}
          rows={shiftDataList ?? []}
          checkboxSelection={false}
          rowSelectionModel={selectedRows}
          onRowClick={(data) => {
            setSelectedRows((prev) => {
              const updated = prev.includes(data.row.shiftRefID)
                ? prev.filter((i) => i !== data.row.shiftRefID)
                : [data.row.shiftRefID];
              return updated;
            });
          }}
          disableAdditionalLoading
          disableSearch
          autoPageSize={false}
          tableStyle={{
            '&.MuiDataGrid-root': { padding: 0 },
            ".MuiButtonBase-root[aria-label='Export']": {
              display: 'none!important',
            },
          }}
          loading={isLoading || isFetching}
          skeletonStyle={{
            minWidth: '800px',
          }}
        />
      </Stack>
    </ModalWrapper>
  );
};

export default POSShiftReportModal;
